import React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CustomerLayout } from '../components/CustomerLayout';
import api, { consumerAPI, publicShopsAPI } from '../services/api';
import { FitSessionModal as EmbeddedFitSessionModal } from '@fit/modal';
import { Pencil, Check, X } from 'lucide-react';

const resolveBackendBase = (): string => {
  // Prefer explicit env, fallback to runtime rule for fit.brendr.io
  const envBase = (import.meta as any).env?.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || '';
  let raw = String(envBase || '').trim();
  try {
    if (typeof window !== 'undefined') {
      const host = window.location.host;
      if (host === 'fit.brendr.io') {
        raw = 'https://fit-production.up.railway.app';
      }
    }
  } catch {}
  if (!raw) return '';
  return raw.replace(/\/$/, '');
};

const ensureServerBase = (maybeApiBase: string): string => {
  // Convert an API base (possibly ending with /api) into server base
  const s = String(maybeApiBase || '').trim();
  if (!s) return '';
  if (/\/api\/?$/.test(s)) return s.replace(/\/api\/?$/, '');
  return s;
};

const DEFAULT_SHOP_ID = '1c0536c3-26b8-4844-85cb-d575e30f54e6';
const SHOW_SHOP_ID_EDITOR = false;

const CustomerFitWidget: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [error, setError] = useState<string>('');
  const [retailerData, setRetailerData] = useState<{ id: string; shopName: string; logoUrl: string; primaryColor: string; apiKey: string }>({ id: 'portal', shopName: 'Klantportaal', logoUrl: '', primaryColor: '#f97316', apiKey: '' });
  const [brandingHideLogo, setBrandingHideLogo] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftShopId, setDraftShopId] = useState('');

  const shopId = useMemo(() => params.get('shopId') || DEFAULT_SHOP_ID, [params]);
  const category = useMemo(() => (params.get('category') || '').toUpperCase(), [params]);

  useEffect(() => {
    try {
      const token = localStorage.getItem('fit_token');
      const user = localStorage.getItem('fit_user');
      if (token) {
        try { sessionStorage.setItem('fit_user_token', token); } catch {}
        try { sessionStorage.setItem('fit_token', token); } catch {}
        try { sessionStorage.setItem('fit_logged_in', 'true'); } catch {}
        try { sessionStorage.removeItem('fit_guest_selected'); } catch {}
      }
      if (user) {
        try { sessionStorage.setItem('fit_user', user); } catch {}
      }
      try {
        const parsed = user ? JSON.parse(user) : null;
        window.dispatchEvent(new CustomEvent('fit-user-updated' as any, { detail: parsed } as any));
      } catch {}
    } catch {}
  }, []);

  // Access control: only ADMIN or PAYED users
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await consumerAPI.getProfile();
        const profile = (resp.data && (resp.data as any).profile) || null;
        const userType = (profile?.user_type || '').toString().toUpperCase();
        const ok = userType === 'ADMIN' || userType === 'PAYED';
        if (mounted) {
          setAllowed(ok);
          if (!ok) {
            setError('Geen toegang. Deze pagina is alleen beschikbaar voor ADMIN of PAYED gebruikers.');
          }
        }
      } catch (e: any) {
        // On unauthorized, redirect to consumer login with next
        try {
          const next = encodeURIComponent('/customer/fit-widget' + (shopId ? `?shopId=${encodeURIComponent(shopId)}` : ''));
          navigate(`/login/consumer?next=${next}`);
          return;
        } catch {}
        setError('Niet geautoriseerd. Log eerst in.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [navigate, shopId]);

  // Compute server base for widget API calls from axios baseURL
  const serverBase = useMemo(() => {
    try {
      const base = api?.defaults?.baseURL || resolveBackendBase();
      return ensureServerBase(String(base || ''));
    } catch {
      return ensureServerBase(resolveBackendBase());
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!serverBase) return;
        const r1 = await fetch(`${serverBase}/api/consumer/profile`, { credentials: 'include' });
        const j1 = await r1.json().catch(() => ({} as any));
        if (!j1 || !j1.success) return;
        const r2 = await fetch(`${serverBase}/api/auth-supabase/mint-widget-token`, { method: 'POST', credentials: 'include' });
        const j2 = await r2.json().catch(() => ({} as any));
        if (cancelled) return;
        if (j2 && j2.success && j2.token) {
          try { sessionStorage.setItem('fit_user_token', String(j2.token)); } catch {}
          try { sessionStorage.setItem('fit_token', String(j2.token)); } catch {}
          try { sessionStorage.setItem('fit_logged_in', 'true'); } catch {}
          try { if (j2.user) sessionStorage.setItem('fit_user', JSON.stringify(j2.user)); } catch {}
          try { window.dispatchEvent(new CustomEvent('fit-user-updated' as any, { detail: j2.user } as any)); } catch {}
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [serverBase]);

  // Load shop branding for header (shop name, logo) using plan-aware backend config
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!shopId) return;
        const resp = await api.get('/widget/branding-config', { params: { shopId } });
        const data = (resp?.data || {}) as any;
        if (!data?.success || !data) return;
        if (cancelled) return;
        setRetailerData(prev => ({
          ...prev,
          id: String(shopId),
          shopName: String(data.shopName || prev.shopName || 'Webshop'),
          logoUrl: String(data.logoUrl || ''),
        }));
        setBrandingHideLogo(!!data.brandingHideLogo);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [shopId]);

  if (loading) {
    return (
      <CustomerLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
        </div>
      </CustomerLayout>
    );
  }

  if (!allowed) {
    return (
      <CustomerLayout>
        <div className="max-w-3xl mx-auto py-10 px-4">
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
            {error || 'Geen toegang tot deze pagina.'}
          </div>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <div className="max-w-5xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Config veld voor shopId */}
        {SHOW_SHOP_ID_EDITOR && (
          <div className="bg-white border border-gray-200 rounded-xl shadow p-4 mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Shop ID | Customer dashboard</label>
            <div className="flex gap-3 items-center">
              <input
                type="text"
                className="form-input flex-1"
                value={isEditing ? draftShopId : shopId}
                readOnly={!isEditing}
                placeholder="Voer Shop UUID in"
                onChange={isEditing ? (e) => setDraftShopId(e.target.value) : undefined}
              />
              {!isEditing && (
                <button
                  type="button"
                  onClick={() => { setDraftShopId(shopId); setIsEditing(true); }}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <Pencil className="h-4 w-4" />
                  Bewerken
                </button>
              )}
              {isEditing && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => { try { const q = new URLSearchParams(params.toString()); if (draftShopId) q.set('shopId', draftShopId); else q.delete('shopId'); setParams(q, { replace: true }); } catch {}; setIsEditing(false); }}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                  >
                    <Check className="h-4 w-4" />
                    Opslaan
                  </button>
                  <button
                    type="button"
                    onClick={() => { setIsEditing(false); setDraftShopId(''); }}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <X className="h-4 w-4" />
                    Annuleren
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="embedded-fit max-w-3xl mx-auto">
          <div className="bg-white border border-gray-200 rounded-xl shadow overflow-hidden p-0">
          <EmbeddedFitSessionModal
            retailerData={retailerData}
            // Empty product prevents auto-filling slot #1
            productData={{ id: '', title: '', price: '', imageUrl: '', url: '' }}
            apiUrl={serverBase}
            category={category || undefined}
            context={{ shopId: shopId || null, retailerId: null, isPortalEmbed: true }}
            brandingHideLogo={brandingHideLogo}
          />
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default CustomerFitWidget;
